import Eyebrow from "@/components/Eyebrow";
import ContactForm from "@/components/ContactForm";
import { usePageMeta, useScrollReveal, COMPANY } from "@/lib/site";
import { Mail, Phone, MapPin, Building2, Clock } from "lucide-react";

export default function Contact() {
  usePageMeta(
    "Kontakt — G&G accounting",
    "Účetní firma v Brně-Slatině. Napište nám nebo zavolejte — rádi se s vámi nezávazně sejdeme a probereme vaše účetnictví."
  );
  useScrollReveal();

  return (
    <div>
      <section className="px-6 md:px-12 pt-20 md:pt-28 pb-16 bg-cream">
        <div className="max-w-7xl mx-auto reveal">
          <Eyebrow>Kontakt</Eyebrow>
          <h1 className="font-display text-5xl md:text-7xl tracking-tighter font-medium mt-6 leading-[0.98] max-w-4xl">
            Pojďme se <span className="text-magenta">poznat.</span>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-muted2 leading-relaxed max-w-2xl">
            Napište, zavolejte, nebo si rovnou domluvte krátkou nezávaznou konzultaci. Reagujeme obvykle
            do jednoho pracovního dne.
          </p>
        </div>
      </section>

      <section className="px-6 md:px-12 py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12">
          {/* Details */}
          <div className="lg:col-span-5 reveal">
            <h2 className="font-display text-2xl md:text-3xl font-medium mb-8 tracking-tight">
              Kontaktní údaje
            </h2>
            <ul className="space-y-6 text-base">
              <li className="flex items-start gap-4">
                <span className="mt-1 inline-flex items-center justify-center w-9 h-9 rounded-md bg-magenta/10 shrink-0">
                  <MapPin className="w-4 h-4 text-magenta" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted2 mb-1">Sídlo</div>
                  <div className="font-medium">{COMPANY.address}</div>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="mt-1 inline-flex items-center justify-center w-9 h-9 rounded-md bg-magenta/10 shrink-0">
                  <Phone className="w-4 h-4 text-magenta" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted2 mb-1">Telefon</div>
                  <a href={`tel:${COMPANY.phone.replace(/\s/g, "")}`} className="font-medium hover:text-magenta transition-colors" data-testid="contact-phone-link">
                    {COMPANY.phone}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="mt-1 inline-flex items-center justify-center w-9 h-9 rounded-md bg-magenta/10 shrink-0">
                  <Mail className="w-4 h-4 text-magenta" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted2 mb-1">E-mail</div>
                  <a href={`mailto:${COMPANY.email}`} className="font-medium hover:text-magenta transition-colors break-all" data-testid="contact-email-link">
                    {COMPANY.email}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="mt-1 inline-flex items-center justify-center w-9 h-9 rounded-md bg-magenta/10 shrink-0">
                  <Clock className="w-4 h-4 text-magenta" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted2 mb-1">Otevírací doba</div>
                  <div className="font-medium">{COMPANY.hours}</div>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="mt-1 inline-flex items-center justify-center w-9 h-9 rounded-md bg-magenta/10 shrink-0">
                  <Building2 className="w-4 h-4 text-magenta" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted2 mb-1">IČO</div>
                  <div className="font-medium">{COMPANY.ico}</div>
                </div>
              </li>
            </ul>

            <div className="mt-12 pt-8 border-t border-line">
              <div className="text-xs uppercase tracking-[0.2em] text-magenta font-semibold mb-5">
                Kontaktní osoby
              </div>
              <div className="space-y-5">
                <div>
                  <div className="font-medium">Nikola Bystřická</div>
                  <div className="text-sm text-muted2">Jednatelka</div>
                  <div className="mt-1 flex flex-col gap-0.5">
                    <a href={`mailto:${COMPANY.emailBystricka}`} className="text-sm font-medium hover:text-magenta transition-colors">
                      {COMPANY.emailBystricka}
                    </a>
                    <a href={`tel:${COMPANY.phoneBystricka.replace(/\s/g, "")}`} className="text-sm font-medium hover:text-magenta transition-colors">
                      {COMPANY.phoneBystricka}
                    </a>
                  </div>
                </div>
                <div>
                  <div className="font-medium">Lenka Dočkalová</div>
                  <div className="text-sm text-muted2">Účetní</div>
                  <div className="mt-1 flex flex-col gap-0.5">
                    <a href={`mailto:${COMPANY.emailDockalova}`} className="text-sm font-medium hover:text-magenta transition-colors">
                      {COMPANY.emailDockalova}
                    </a>
                    <a href={`tel:${COMPANY.phoneDockalova.replace(/\s/g, "")}`} className="text-sm font-medium hover:text-magenta transition-colors">
                      {COMPANY.phoneDockalova}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7 reveal">
            <ContactForm />
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="bg-white pb-0">
        <div className="w-full h-[420px] md:h-[520px] overflow-hidden bg-cream">
          <iframe
            title="Mapa - G&G accounting, Hviezdoslavova 1092/4, Brno-Slatina"
            src="https://maps.google.com/maps?q=Hviezdoslavova%201092%2F4%2C%20627%2000%20Brno-Slatina&t=m&z=15&ie=UTF8&iwloc=&output=embed"
            className="w-full h-full block"
            style={{ filter: "grayscale(1) contrast(1.05)", border: 0 }}
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="contact-map"
          />
        </div>
      </section>
    </div>
  );
}
